import { ResourceGrid } from "../components/resources-grid";
import { resource } from "../objects/resource";
import {
  Text,
  ITextProps,
  Pivot,
  PivotItem,
  Icon,
  mergeStyleSets,
  mergeStyles,
} from "@fluentui/react";
import { ChangeType } from "../enums";

interface resourceListProps {
  ownedResources: Array<resource>;
  refreshDatasetFunction: (
    resourceID: string,
    changeType: ChangeType,
    value: any,
  ) => void;
}

// ── Styles ───────────────────────────────────────────────────────────────────
const styles = mergeStyleSets({
  container: {
    marginTop: "0px",
  },
  tabContent: {
    paddingTop: "16px",
  },
  headerTab: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "20px",
    height: "20px",
    padding: "0 6px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: "20px",
  },
  badgeSPO: {
    background: "#e0f5f5",
    color: "#038387",
  },
  badgeTeams: {
    background: "#ebebf5",
    color: "#464775",
  },
  badgeYammer: {
    background: "#ddeeff",
    color: "#135995",
  },
  emptyState: {
    padding: "40px 0",
    textAlign: "center" as const,
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "12px",
    display: "block",
  },
});

// ── Tab header renderer ───────────────────────────────────────────────────────
interface TabHeaderProps {
  iconName: string;
  iconColor: string;
  label: string;
  count: number;
  badgeClass: string;
}

const TabHeader: React.FC<TabHeaderProps> = ({
  iconName,
  iconColor,
  label,
  count,
  badgeClass,
}) => (
  <div className={styles.headerTab}>
    <Icon
      iconName={iconName}
      styles={{
        root: {
          fontSize: 18,
          color: iconColor,
        },
      }}
    />
    <Text
      variant={"medium" as ITextProps["variant"]}
      styles={{ root: { fontWeight: 600 } }}
    >
      {label}
    </Text>
    <span className={`${styles.badge} ${badgeClass}`}>{count}</span>
  </div>
);

// ── Empty state renderer ──────────────────────────────────────────────────────
interface EmptyStateProps {
  iconName: string;
  iconColor: string;
  message: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  iconName,
  iconColor,
  message,
}) => (
  <div className={styles.emptyState}>
    <Icon
      iconName={iconName}
      className={styles.emptyIcon}
      styles={{ root: { color: iconColor, fontSize: 48 } }}
    />
    <Text variant="medium" styles={{ root: { color: "#605e5c" } }}>
      {message}
    </Text>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────
export function ResourceGridByProduct(props: resourceListProps) {
  const siteList = props.ownedResources.filter(
    (p) => p.resourceType === "SPO" || p.resourceType === "CSPO",
  );
  const teamsList = props.ownedResources.filter(
    (p) => p.resourceType === "Teams",
  );
  const yammerList = props.ownedResources.filter(
    (p) => p.resourceType === "Yammer",
  );

  return (
    <div className={styles.container}>
      <Pivot
        aria-label="Workspaces by product"
        styles={{
          root: {
            borderBottom: "1px solid #e1dfdd",
            marginBottom: "4px",
          },
          link: {
            height: "48px",
            selectors: {
              ":hover": {
                backgroundColor: "#f3f2f1",
              },
            },
          },
          linkIsSelected: {
            height: "48px",
            selectors: {
              // Selected tab bottom border colour — uses Teams blue as default
              "::before": {
                backgroundColor: "#0078d4",
              },
            },
          },
        }}
      >
        {/* ── SharePoint Online ─────────────────────────────────────────── */}
        <PivotItem
          headerText=""
          onRenderItemLink={() => (
            <TabHeader
              iconName="SharepointLogo"
              iconColor="#038387"
              label="SharePoint Online"
              count={siteList.length}
              badgeClass={styles.badgeSPO}
            />
          )}
          itemKey="spo"
        >
          <div className={styles.tabContent}>
            {siteList.length > 0 ? (
              <ResourceGrid
                ownedResources={siteList}
                resourceType="SPO"
                refreshDatasetFunction={props.refreshDatasetFunction}
              />
            ) : (
              <EmptyState
                iconName="SharepointLogo"
                iconColor="#038387"
                message="No SharePoint sites found."
              />
            )}
          </div>
        </PivotItem>

        {/* ── Microsoft Teams ───────────────────────────────────────────── */}
        <PivotItem
          headerText=""
          onRenderItemLink={() => (
            <TabHeader
              iconName="TeamsLogo"
              iconColor="#464775"
              label="Microsoft Teams"
              count={teamsList.length}
              badgeClass={styles.badgeTeams}
            />
          )}
          itemKey="teams"
        >
          <div className={styles.tabContent}>
            {teamsList.length > 0 ? (
              <ResourceGrid
                ownedResources={teamsList}
                resourceType="Teams"
                refreshDatasetFunction={props.refreshDatasetFunction}
              />
            ) : (
              <EmptyState
                iconName="TeamsLogo"
                iconColor="#464775"
                message="No Microsoft Teams workspaces found."
              />
            )}
          </div>
        </PivotItem>

        {/* ── Viva Engage (Yammer) ──────────────────────────────────────── */}
        <PivotItem
          headerText=""
          onRenderItemLink={() => (
            <TabHeader
              iconName="YammerLogo"
              iconColor="#135995"
              label="Viva Engage"
              count={yammerList.length}
              badgeClass={styles.badgeYammer}
            />
          )}
          itemKey="yammer"
        >
          <div className={styles.tabContent}>
            {yammerList.length > 0 ? (
              <ResourceGrid
                ownedResources={yammerList}
                resourceType="Yammer"
                refreshDatasetFunction={props.refreshDatasetFunction}
              />
            ) : (
              <EmptyState
                iconName="YammerLogo"
                iconColor="#135995"
                message="No Viva Engage communities found."
              />
            )}
          </div>
        </PivotItem>
      </Pivot>
    </div>
  );
}

export default ResourceGridByProduct;